/*
  Joventra Studio - Auto Currency & Language Display
  --------------------------------------------------
  Detects the visitor's country (via Shopify's own native geo-IP detection,
  passed in from Liquid as window.__jvCountry) and converts every displayed
  price on the storefront from the store's base currency (USD) into the
  visitor's local currency, using live exchange rates.

  IMPORTANT: This changes DISPLAY only. Shopify Payments is not available in
  India, so this store checks out in USD regardless of what is shown here.
  A small note is attached to converted prices to keep this honest.
*/
(function () {
  'use strict';

  var BASE_CURRENCY = (window.__jvShopCurrency || 'USD').toUpperCase();
  var DETECTED_COUNTRY = (window.__jvCountry || '').toUpperCase();
  var RATES_CACHE_KEY = 'jv_fx_rates_v1';
  var RATES_MAX_AGE_MS = 6 * 60 * 60 * 1000; // 6 hours
  var MANUAL_OVERRIDE_KEY = 'jv_currency_override';

  // ---- Country (ISO 3166-1 alpha-2) -> Currency (ISO 4217) ----
  var COUNTRY_CURRENCY = {
    // North America
    US: 'USD', CA: 'CAD', MX: 'MXN', BM: 'BMD', JM: 'JMD', TT: 'TTD', BS: 'BSD',
    BB: 'BBD', DO: 'DOP', CR: 'CRC', GT: 'GTQ', HN: 'HNL', NI: 'NIO', PA: 'PAB',
    SV: 'USD', BZ: 'BZD', HT: 'HTG', CU: 'CUP', EC: 'USD',
    // South America
    BR: 'BRL', AR: 'ARS', CL: 'CLP', CO: 'COP', PE: 'PEN', VE: 'VES', BO: 'BOB',
    PY: 'PYG', UY: 'UYU', GY: 'GYD', SR: 'SRD',
    // Eurozone + nearby EUR users
    AT: 'EUR', BE: 'EUR', HR: 'EUR', CY: 'EUR', EE: 'EUR', FI: 'EUR', FR: 'EUR',
    DE: 'EUR', GR: 'EUR', IE: 'EUR', IT: 'EUR', LV: 'EUR', LT: 'EUR', LU: 'EUR',
    MT: 'EUR', NL: 'EUR', PT: 'EUR', SK: 'EUR', SI: 'EUR', ES: 'EUR', AD: 'EUR',
    MC: 'EUR', SM: 'EUR', VA: 'EUR', ME: 'EUR', XK: 'EUR',
    // Rest of Europe
    GB: 'GBP', CH: 'CHF', NO: 'NOK', SE: 'SEK', DK: 'DKK', IS: 'ISK', PL: 'PLN',
    CZ: 'CZK', HU: 'HUF', RO: 'RON', BG: 'BGN', RS: 'RSD', BA: 'BAM', MK: 'MKD',
    AL: 'ALL', UA: 'UAH', BY: 'BYN', MD: 'MDL', RU: 'RUB', TR: 'TRY', GE: 'GEL',
    AM: 'AMD', AZ: 'AZN',
    // Asia
    IN: 'INR', CN: 'CNY', JP: 'JPY', KR: 'KRW', ID: 'IDR', MY: 'MYR', SG: 'SGD',
    TH: 'THB', VN: 'VND', PH: 'PHP', PK: 'PKR', BD: 'BDT', LK: 'LKR', NP: 'NPR',
    MM: 'MMK', KH: 'KHR', LA: 'LAK', MN: 'MNT', KZ: 'KZT', UZ: 'UZS', TW: 'TWD',
    HK: 'HKD', MO: 'MOP', BT: 'BTN', MV: 'MVR', BN: 'BND', AF: 'AFN', IQ: 'IQD',
    IR: 'IRR', IL: 'ILS', JO: 'JOD', LB: 'LBP', SY: 'SYP', SA: 'SAR', AE: 'AED',
    QA: 'QAR', KW: 'KWD', BH: 'BHD', OM: 'OMR', YE: 'YER', PS: 'ILS',
    // Africa
    ZA: 'ZAR', NG: 'NGN', EG: 'EGP', KE: 'KES', GH: 'GHS', ET: 'ETB', TZ: 'TZS',
    UG: 'UGX', DZ: 'DZD', MA: 'MAD', TN: 'TND', LY: 'LYD', SD: 'SDG',
    SN: 'XOF', CI: 'XOF', ML: 'XOF', BF: 'XOF', NE: 'XOF', TG: 'XOF', BJ: 'XOF', GW: 'XOF',
    CM: 'XAF', GA: 'XAF', CG: 'XAF', TD: 'XAF', CF: 'XAF', GQ: 'XAF',
    CD: 'CDF', AO: 'AOA', MZ: 'MZN', ZM: 'ZMW', ZW: 'ZWL', BW: 'BWP', NA: 'NAD',
    MU: 'MUR', MG: 'MGA', RW: 'RWF', BI: 'BIF', SO: 'SOS', DJ: 'DJF', ER: 'ERN',
    SS: 'SSP', LS: 'LSL', SZ: 'SZL', CV: 'CVE', GM: 'GMD', GN: 'GNF', LR: 'LRD',
    SL: 'SLE', ST: 'STN', KM: 'KMF', SC: 'SCR',
    // Oceania
    AU: 'AUD', NZ: 'NZD', FJ: 'FJD', PG: 'PGK', WS: 'WST', TO: 'TOP', VU: 'VUV',
    SB: 'SBD', NC: 'XPF', PF: 'XPF'
  };

  var SYMBOLS = {
    USD: '$', EUR: '€', GBP: '£', INR: '₹', JPY: '¥', CNY: '¥',
    KRW: '₩', AUD: 'A$', CAD: 'C$', NZD: 'NZ$', CHF: 'Fr', SEK: 'kr', NOK: 'kr',
    DKK: 'kr', PLN: 'zł', CZK: 'Kč', HUF: 'Ft', RON: 'lei', BGN: 'лв',
    RUB: '₽', TRY: '₺', BRL: 'R$', ARS: '$', MXN: '$', ZAR: 'R', NGN: '₦',
    EGP: 'E£', KES: 'KSh', GHS: 'GH₵', IDR: 'Rp', MYR: 'RM', SGD: 'S$',
    THB: '฿', VND: '₫', PHP: '₱', PKR: '₨', BDT: '৳', LKR: 'Rs',
    HKD: 'HK$', TWD: 'NT$', AED: 'AED ', SAR: 'SAR ', ILS: '₪', UAH: '₴',
    QAR: 'QAR ', KWD: 'KWD ', BHD: 'BHD ', OMR: 'OMR ', VES: 'Bs'
  };

  var ZERO_DECIMAL = { BIF: 1, CLP: 1, DJF: 1, GNF: 1, ISK: 1, JPY: 1, KMF: 1, KRW: 1,
    PYG: 1, RWF: 1, UGX: 1, VND: 1, VUV: 1, XAF: 1, XOF: 1, XPF: 1 };

  function getTargetCurrency() {
    try {
      var override = localStorage.getItem(MANUAL_OVERRIDE_KEY);
      if (override) return override;
    } catch (e) {}
    return COUNTRY_CURRENCY[DETECTED_COUNTRY] || BASE_CURRENCY;
  }

  function formatAmount(value, currency) {
    var decimals = ZERO_DECIMAL[currency] ? 0 : 2;
    var fixed = value.toFixed(decimals);
    var parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var num = parts.join('.');
    var symbol = SYMBOLS[currency] || (currency + ' ');
    return symbol + num;
  }

  function fetchRates(cb) {
    try {
      var cached = JSON.parse(localStorage.getItem(RATES_CACHE_KEY) || 'null');
      if (cached && cached.base === BASE_CURRENCY && (Date.now() - cached.ts) < RATES_MAX_AGE_MS) {
        cb(cached.rates);
        return;
      }
    } catch (e) {}

    var url = 'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/' +
      BASE_CURRENCY.toLowerCase() + '.json';

    fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var rates = data[BASE_CURRENCY.toLowerCase()];
        if (!rates) throw new Error('no rates');
        try {
          localStorage.setItem(RATES_CACHE_KEY, JSON.stringify({ base: BASE_CURRENCY, ts: Date.now(), rates: rates }));
        } catch (e) {}
        cb(rates);
      })
      .catch(function () {
        // Fallback provider
        fetch('https://open.er-api.com/v6/latest/' + BASE_CURRENCY)
          .then(function (r) { return r.json(); })
          .then(function (data) {
            if (!data.rates) throw new Error('no rates');
            var rates = {};
            Object.keys(data.rates).forEach(function (k) { rates[k.toLowerCase()] = data.rates[k]; });
            try {
              localStorage.setItem(RATES_CACHE_KEY, JSON.stringify({ base: BASE_CURRENCY, ts: Date.now(), rates: rates }));
            } catch (e) {}
            cb(rates);
          })
          .catch(function () {
            try {
              var stale = JSON.parse(localStorage.getItem(RATES_CACHE_KEY) || 'null');
              if (stale) { cb(stale.rates); return; }
            } catch (e) {}
            cb(null);
          });
      });
  }

  function parseUsdValue(text) {
    var match = text.replace(/,/g, '').match(/(\d+(\.\d+)?)/);
    if (!match) return null;
    return parseFloat(match[1]);
  }

  function convertAndRender(rates) {
    var target = getTargetCurrency();
    if (target === BASE_CURRENCY) {
      // Still label as base currency, remove any earlier conversion visuals.
      document.querySelectorAll('.money[data-jv-usd]').forEach(function (el) {
        var usd = parseFloat(el.getAttribute('data-jv-usd'));
        el.textContent = formatAmount(usd, BASE_CURRENCY);
        el.removeAttribute('title');
      });
      updateBadge(BASE_CURRENCY);
      return;
    }

    var rate = rates ? rates[target.toLowerCase()] : null;
    if (!rate) { updateBadge(null); return; }

    var nodes = document.querySelectorAll('.money');
    nodes.forEach(function (el) {
      var usd = el.hasAttribute('data-jv-usd')
        ? parseFloat(el.getAttribute('data-jv-usd'))
        : parseUsdValue(el.textContent);
      if (usd === null || isNaN(usd)) return;
      if (!el.hasAttribute('data-jv-usd')) el.setAttribute('data-jv-usd', usd);
      el.textContent = formatAmount(usd * rate, target);
      el.setAttribute('title', 'Estimated price. You will be charged in ' + BASE_CURRENCY + ' at checkout.');
    });

    updateBadge(target);
  }

  function updateBadge(currency) {
    var badge = document.getElementById('jv-currency-badge');
    if (!badge) return;
    if (!currency) { badge.style.display = 'none'; return; }
    badge.style.display = '';
    badge.textContent = currency + ' ≈';
  }

  function createBadge() {
    if (document.getElementById('jv-currency-badge')) return;
    var badge = document.createElement('div');
    badge.id = 'jv-currency-badge';
    badge.title = 'Prices are shown as an estimate in your local currency. You are charged in ' + BASE_CURRENCY + ' at checkout.';
    badge.style.cssText = 'position:fixed;left:14px;bottom:14px;z-index:9998;' +
      'background:rgba(31,31,31,0.85);color:#fff;font-size:11px;font-weight:600;' +
      'padding:6px 10px;border-radius:20px;font-family:inherit;letter-spacing:.02em;' +
      'pointer-events:none;display:none;';
    document.body.appendChild(badge);
  }

  var runScheduled = false;
  function scheduleRun(rates) {
    if (runScheduled) return;
    runScheduled = true;
    requestAnimationFrame(function () {
      runScheduled = false;
      convertAndRender(rates);
    });
  }

  function init() {
    createBadge();
    fetchRates(function (rates) {
      convertAndRender(rates);
      var observer = new MutationObserver(function () { scheduleRun(rates); });
      observer.observe(document.body, { childList: true, subtree: true });
      window.addEventListener('cart:update', function () { scheduleRun(rates); });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
